import { useState } from "react";
import { CartHeader } from "components/cart/CartHeader";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import CartBannerStrip from "components/common/notification-strips/CartBannerStrip";
import UpsellSkeleton from "components/common/skeleton/UpsellSkeleton";
import UpSellAndWishlistSection from "components/upsell/UpSellSection";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantUiConfig } from "lib/contexts/MerchantUIConfigProvider";
import { useLocale } from "lib/hooks/useLocale";
import { BannerType } from "lib/types/checkout";
import { UpsellAndWishlistGroupsOnDiscountType } from "lib/types/upsell";
import { eventTypes } from "lib/utils/constants";
import { classNames, publishPostMessage, redirectUrl } from "lib/utils/helpers";
import cartImage from "assests/images/cart.png";
import TrustBadgeGray from "assests/images/trust-badge-gray.svg";
import { useRecentViewData } from "lib/hooks/useRecentlyViewed";

export const EmptyCart = () => {
  const { t } = useLocale();

  const [showEmptyState, setShowEmptyState] = useState(false);
  const { upsellData, isLoading } = useRecentViewData();

  const checkoutContext = useCheckoutContext();
  const {
    actions: { setCheckoutModal },
  } = checkoutContext;

  const {
    state: {
      cartMetadata: { metadataLoading, ui_attributes },
    },
  } = useMerchantUiConfig();
  const { empty_cart_cta_text, empty_cart_cta_url } = ui_attributes ?? {};

  const { empty_cart_upsell_enabled } = ui_attributes ?? {};

  const closeCart = () => {
    setCheckoutModal("NONE");
    publishPostMessage(eventTypes.CLOSE_CART_IFRAME, {});
  };

  const separator = (
    <div className="relative flex items-center justify-center mt-4">
      <span className="px-4 text-gray-dark font-medium before:content-[''] before:absolute before:left-[0] before:right-[55%] before:top-1/2 before:h-[1px] before:bg-gray-light after:content-[''] after:absolute after:left-[55%] after:right-[0] after:top-1/2 after:h-[1px] after:bg-gray-light">
        OR
      </span>
    </div>
  );

  const dynamicButton = (
    <div className="flex flex-col items-center justify-center">
      <PrimaryButton
        buttonText={
          metadataLoading ? "" : empty_cart_cta_text?.length ? empty_cart_cta_text : t("continue_shopping")
        }
        containerClassName="p-4"
        width="w-[228px]"
        onClick={() => {
          try {
            const url = new URL(empty_cart_cta_url ?? "");
            if (url.protocol === "http:") {
              url.protocol = "https:";
            }
            redirectUrl(url.toString());
          } catch {
            closeCart();
          }
        }}
        isDisabled={metadataLoading}
        isLoading={metadataLoading}
      />
    </div>
  );

  const renderCartBody = () => {
    const isLoadingState = isLoading || metadataLoading;

    if (isLoadingState) {
      return (
        <>
          <UpsellSkeleton />
          {separator}
          {dynamicButton}
        </>
      );
    }

    if (showEmptyState || !empty_cart_upsell_enabled) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <div className="flex flex-col items-center space-y-2">
            <img src={cartImage} className="" alt="cart" />
            <div className="text-sm font-medium text-coal-light ">{t("cart_is_empty")}</div>
          </div>
          {dynamicButton}
        </div>
      );
    }

    if (empty_cart_upsell_enabled) {
      return (
        <>
          <UpSellAndWishlistSection
            parent="EMPTY_CART"
            customTabs={
              upsellData?.length
                ? [
                    {
                      value: UpsellAndWishlistGroupsOnDiscountType.RECENTLY_VIEWED,
                      title: t("recently_viewed"),
                      products: upsellData,
                    },
                  ]
                : []
            }
            onUpsellDataParsed={(data) => {
              if (
                !data.discountedUpsells.length &&
                !data.nonDiscountedUpsells.length &&
                !upsellData?.length
              ) {
                setShowEmptyState(true);
              }
            }}
          />
          {separator}
          {dynamicButton}
        </>
      );
    }

    return dynamicButton;
  };

  return (
    <div className="items-between flex h-full flex-col bg-white relative">
      <CartHeader />
      <div className="h-[1px] w-full bg-gray-light" />
      {metadataLoading ? (
        <div className="skeleton-loader sticky top-0 z-20 flex  min-h-[1.875rem]  w-full"></div>
      ) : (
        <CartBannerStrip bannerPosition={BannerType.HEADER} />
      )}
      <div className={classNames("flex flex-col flex-1 mx-3 mt-4")}>{renderCartBody()}</div>
      <div className="flex-1 flex flex-col items-center justify-end">
        <img src={TrustBadgeGray} className="mb-14 h-4" alt={"shopflo-logo"} />
      </div>
    </div>
  );
};
